// src/pages/ProfilePage.js
import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'react-qr-code';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  documentId,
  serverTimestamp,
} from 'firebase/firestore';
import { getFavoriteCafeIds, saveFavoriteCafeIds } from '../utils/favoriteCafes';
import { isReservationActive, listReservationsByUser } from '../utils/reservations';

const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export default function ProfilePage() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 900);
  const [isPhone, setIsPhone] = useState(window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => {
      setIsNarrow(window.innerWidth < 900);
      setIsPhone(window.innerWidth < 640);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [initial, setInitial] = useState({ name: '', phone: '' });

  const [readBooks, setReadBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(true);
  const [balance, setBalance] = useState(0);
  const [uniqueAdded, setUniqueAdded] = useState(0);
  const [booksTaken, setBooksTaken] = useState(0);

  const [favoriteCafes, setFavoriteCafes] = useState([]);
  const [loadingFavoriteCafes, setLoadingFavoriteCafes] = useState(true);
  const [favoriteBusyId, setFavoriteBusyId] = useState(null);
  const [confirmRemoveCafe, setConfirmRemoveCafe] = useState(null);

  const [activeReservations, setActiveReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isQrPreviewOpen, setIsQrPreviewOpen] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;

    (async () => {
      const ref = doc(db, 'users', user.uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          phone: '',
          role: 'user',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setName(user.displayName || '');
        setPhone('');
        setInitial({ name: user.displayName || '', phone: '' });
        setBalance(0);
        setUniqueAdded(0);
        setBooksTaken(0);
        return;
      }
      const data = snap.data() || {};
      const nm = data.displayName ?? user.displayName ?? '';
      const ph = data.phone ?? data.phoneNumber ?? '';
      setName(nm);
      setPhone(ph);
      setInitial({ name: nm, phone: ph });
      setBalance(typeof data.balance === 'number' ? data.balance : 0);
      setUniqueAdded(typeof data.uniqueAdded === 'number' ? data.uniqueAdded : 0);
      setBooksTaken(typeof data.booksTaken === 'number' ? data.booksTaken : 0);
    })();

    (async () => {
      try {
        const q = query(
          collection(db, 'borrows'),
          where('userUid', '==', user.uid),
          where('action', '==', 'take'),
          orderBy('createdAt', 'desc'),
          limit(8),
        );
        const snaps = await getDocs(q);
        const borrows = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));

        const bookIds = Array.from(new Set(borrows.map((b) => b.bookId).filter(Boolean)));
        const bookPairs = await Promise.all(
          bookIds.map(async (id) => {
            const s = await getDoc(doc(db, 'books', id));
            return [id, s.exists() ? { id: s.id, ...s.data() } : null];
          }),
        );
        const bookMap = new Map(bookPairs);

        const templateIds = Array.from(
          new Set(
            borrows
              .map((b) => b.templateId || bookMap.get(b.bookId)?.templateId)
              .filter(Boolean),
          ),
        );
        const tplMap = new Map();
        for (const pack of chunk(templateIds, 10)) {
          const s = await getDocs(query(collection(db, 'bookTemplates'), where(documentId(), 'in', pack)));
          s.docs.forEach((d) => tplMap.set(d.id, { id: d.id, ...d.data() }));
        }

        const enriched = borrows.map((b) => {
          const book = bookMap.get(b.bookId) || {};
          const tpl = tplMap.get(b.templateId || book.templateId) || {};
          const cover =
            b.coverUrl ||
            book.coverUrl ||
            book.cover ||
            tpl.coverUrl ||
            tpl.cover ||
            tpl.imageUrl ||
            tpl.thumbnailUrl ||
            '';
          return {
            ...b,
            isbn: b.isbn || book.isbn || tpl.isbn || '',
            title: b.title || book.title || tpl.title || '\u0411\u0435\u0437 \u043D\u0430\u0437\u0432\u0438',
            author: b.author || book.author || tpl.author || '\u0410\u0432\u0442\u043E\u0440 \u043D\u0435\u0432\u0456\u0434\u043E\u043C\u0438\u0439',
            coverUrl: cover,
          };
        });

        setReadBooks(enriched);
      } catch {
        setReadBooks([]);
      } finally {
        setLoadingBooks(false);
      }
    })();
  }, [user?.uid, user?.displayName, user?.email]);

  useEffect(() => {
    (async () => {
      if (!user?.uid) {
        setFavoriteCafes([]);
        setLoadingFavoriteCafes(false);
        return;
      }
      setLoadingFavoriteCafes(true);
      try {
        const ids = await getFavoriteCafeIds(user.uid);
        if (!ids.length) {
          setFavoriteCafes([]);
          return;
        }
        const result = [];
        for (const pack of chunk(ids, 10)) {
          const cafesSnap = await getDocs(query(collection(db, 'cafes'), where(documentId(), 'in', pack)));
          cafesSnap.docs.forEach((d) => {
            const x = d.data() || {};
            result.push({
              id: d.id,
              name: x.cafeName || '\u0411\u0435\u0437 \u043D\u0430\u0437\u0432\u0438',
              address: x.cafeAddress || '',
              mapsLink: x.mapsLink || '',
              lat: typeof x.lat === 'number' ? x.lat : null,
              lng: typeof x.lng === 'number' ? x.lng : null,
            });
          });
        }
        setFavoriteCafes(result.sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        setFavoriteCafes([]);
      } finally {
        setLoadingFavoriteCafes(false);
      }
    })();
  }, [user?.uid]);

  useEffect(() => {
    (async () => {
      if (!user?.uid) {
        setActiveReservations([]);
        setLoadingReservations(false);
        return;
      }
      setLoadingReservations(true);
      try {
        const now = Date.now();
        const rows = await listReservationsByUser(user.uid, 40);
        const activeRows = (rows || []).filter((r) => isReservationActive(r, now));
        if (!activeRows.length) {
          setActiveReservations([]);
          return;
        }

        const bookIds = Array.from(new Set(activeRows.map((r) => String(r.bookId || '')).filter(Boolean)));
        const bookMap = new Map();
        for (const pack of chunk(bookIds, 10)) {
          const s = await getDocs(query(collection(db, 'books'), where(documentId(), 'in', pack)));
          s.docs.forEach((d) => bookMap.set(d.id, { id: d.id, ...d.data() }));
        }

        const templateIds = Array.from(
          new Set(
            activeRows
              .map((r) => bookMap.get(String(r.bookId || ''))?.templateId)
              .filter(Boolean),
          ),
        );
        const tplMap = new Map();
        for (const pack of chunk(templateIds, 10)) {
          const s = await getDocs(query(collection(db, 'bookTemplates'), where(documentId(), 'in', pack)));
          s.docs.forEach((d) => tplMap.set(d.id, { id: d.id, ...d.data() }));
        }

        const normalized = activeRows
          .map((r) => {
            const book = bookMap.get(String(r.bookId || '')) || {};
            const tpl = tplMap.get(book.templateId) || {};
            return {
              id: r.id || String(r.bookId || ''),
              isbn: book.isbn || tpl.isbn || r.isbn || '',
              title: book.title || tpl.title || '\u0411\u0435\u0437 \u043D\u0430\u0437\u0432\u0438',
              author: book.author || tpl.author || '\u0410\u0432\u0442\u043E\u0440 \u043D\u0435\u0432\u0456\u0434\u043E\u043C\u0438\u0439',
              cafeName: r.cafeName || book.cafeName || '\u041A\u0430\u0432\u2019\u044F\u0440\u043D\u044F',
              cafeAddress: r.cafeAddress || book.cafeAddress || '',
              expiresAtMs: Number(r.expiresAtMs) || 0,
            };
          })
          .sort((a, b) => a.expiresAtMs - b.expiresAtMs);
        setActiveReservations(normalized);
      } catch {
        setActiveReservations([]);
      } finally {
        setLoadingReservations(false);
      }
    })();
  }, [user?.uid]);

  const hasChanges = useMemo(
    () => name !== initial.name || phone !== initial.phone,
    [name, phone, initial],
  );

  const handleSave = async () => {
    if (!user?.uid || !hasChanges) return;
    const ref = doc(db, 'users', user.uid);
    await updateDoc(ref, {
      displayName: name,
      phone,
      updatedAt: serverTimestamp(),
    });
    if (name !== (auth.currentUser?.displayName || '')) {
      await updateProfile(auth.currentUser, { displayName: name });
    }
    setInitial({ name, phone });
    alert('\u0417\u0431\u0435\u0440\u0435\u0436\u0435\u043D\u043E \u2705');
  };

  const openCafeMap = (cafe) => {
    const direct = cafe.mapsLink && cafe.mapsLink.trim();
    let url = direct;
    if (!url) {
      const queryStr =
        cafe.lat != null && cafe.lng != null
          ? `${cafe.lat},${cafe.lng}`
          : `${cafe.name || ''} ${cafe.address || ''}`;
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(queryStr)}`;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const removeFavoriteCafe = async (cafeId) => {
    if (!user?.uid || !cafeId || favoriteBusyId) return;
    setFavoriteBusyId(cafeId);
    try {
      const ids = await getFavoriteCafeIds(user.uid);
      const next = ids.filter((id) => id !== cafeId);
      await saveFavoriteCafeIds(user.uid, next, user.email || '');
      setFavoriteCafes((prev) => prev.filter((c) => c.id !== cafeId));
    } finally {
      setFavoriteBusyId(null);
    }
  };

  const profileInitials = useMemo(() => {
    const source = (name || user?.displayName || user?.email || '').trim();
    if (!source) return 'U';
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    return (parts[0]?.slice(0, 2) || 'U').toUpperCase();
  }, [name, user?.displayName, user?.email]);

  if (!user) {
    return (
      <div style={{ background: '#F6F4EF', minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
        <div style={{ color: '#374151' }}>{'\u0417\u0430\u0432\u0430\u043D\u0442\u0430\u0436\u0435\u043D\u043D\u044F\u2026'}</div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F6F4EF', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 16px' }}>
        <Card>
          <div style={{ padding: isNarrow ? 16 : 20, display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 1.3fr 1.45fr', gap: 20 }}>
            <div style={{ display: 'grid', gap: 10, justifyItems: isPhone ? 'center' : isNarrow ? 'start' : 'center', alignContent: 'start' }}>
              <button
                type="button"
                onClick={() => setIsQrPreviewOpen(true)}
                aria-label={'\u0412\u0456\u0434\u043A\u0440\u0438\u0442\u0438 QR \u043D\u0430 \u0432\u0435\u0441\u044C \u0435\u043A\u0440\u0430\u043D'}
                style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'zoom-in' }}
              >
                <div style={{ background: '#fff', padding: 10, borderRadius: 12, border: '1px solid #e5e7eb' }}>
                  <QRCode value={user.uid} size={isPhone ? 190 : isNarrow ? 140 : 170} />
                </div>
              </button>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{'\u0411\u0430\u043B\u0430\u043D\u0441'}</div>
              <div style={{ fontSize: isNarrow ? 28 : 34, fontWeight: 900, color: '#065f46', lineHeight: 1 }}>{balance}</div>
              <div style={{ color: '#6b7280', textAlign: isPhone ? 'center' : isNarrow ? 'left' : 'center', fontSize: 13 }}>
                {'\u0421\u043A\u0430\u043D\u0443\u0439\u0442\u0435 \u0432 \u043A\u0430\u0432\u2019\u044F\u0440\u043D\u0456 \u0434\u043B\u044F \u0432\u0438\u0434\u0430\u0447\u0456/\u043F\u043E\u0432\u0435\u0440\u043D\u0435\u043D\u043D\u044F \u043A\u043D\u0438\u0433\u0438'}
              </div>
            </div>

            <div style={{ border: '1px solid #d1d5db', borderRadius: 12, padding: 12, background: '#fff' }}>
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: 20 }}>{'\u041C\u043E\u0457 \u0431\u0440\u043E\u043D\u044E\u0432\u0430\u043D\u043D\u044F'}</h3>
                {activeReservations.length > 0 && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      border: '1px solid #86efac',
                      background: '#f8fafc',
                      borderRadius: 999,
                      padding: '3px 9px',
                      color: '#166534',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                    {'\u0410\u043A\u0442\u0438\u0432\u043D\u0435 \u0431\u0440\u043E\u043D\u044E\u0432\u0430\u043D\u043D\u044F'}
                  </span>
                )}
              </div>
              {loadingReservations ? (
                <div style={{ color: '#6b7280' }}>{'\u0417\u0430\u0432\u0430\u043D\u0442\u0430\u0436\u0435\u043D\u043D\u044F\u2026'}</div>
              ) : activeReservations.length === 0 ? (
                <div style={{ color: '#6b7280' }}>{'\u041D\u0435\u043C\u0430\u0454 \u0430\u043A\u0442\u0438\u0432\u043D\u0438\u0445 \u0431\u0440\u043E\u043D\u044E\u0432\u0430\u043D\u044C.'}</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {activeReservations.map((r) => (
                    <div key={r.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 10px', background: '#fff' }}>
                      <div style={{ fontWeight: 700 }}>{r.title}</div>
                      <div style={{ color: '#6b7280', fontSize: 13 }}>{r.author}</div>
                      <div style={{ marginTop: 3, fontSize: 13 }}>
                        {r.cafeName}{r.cafeAddress ? `, ${r.cafeAddress}` : ''}
                      </div>
                      <div style={{ marginTop: 3, color: '#14532d', fontSize: 12 }}>
                        {'\u2705 \u0414\u043E'} {new Date(r.expiresAtMs).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {r.isbn && (
                        <button
                          type="button"
                          onClick={() => navigate(`/book/${encodeURIComponent(r.isbn)}`)}
                          style={{
                            marginTop: 6,
                            padding: '5px 9px',
                            borderRadius: 8,
                            border: '1px solid #0b5226',
                            background: '#fff',
                            color: '#0b5226',
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          {'\u0412\u0456\u0434\u043A\u0440\u0438\u0442\u0438 \u043A\u043D\u0438\u0433\u0443'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '74px 1fr', gap: 14, alignItems: 'start' }}>
              <div
                style={{
                  width: 74,
                  height: 74,
                  borderRadius: '50%',
                  border: '1px solid #d1d5db',
                  background: '#f3f4f6',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 24,
                  fontWeight: 800,
                  color: '#374151',
                }}
              >
                {profileInitials}
              </div>
              <div>
                <div style={{ fontSize: isNarrow ? 24 : 30, fontWeight: 800, lineHeight: 1.1 }}>{name || user.displayName || '\u041A\u043E\u0440\u0438\u0441\u0442\u0443\u0432\u0430\u0447'}</div>
                <div style={{ marginTop: 8, color: '#4b5563', fontSize: 14 }}>{user.email || '\u2014'}</div>
                <div style={{ marginTop: 4, color: '#4b5563', fontSize: 14 }}>{phone || '\u0422\u0435\u043B\u0435\u0444\u043E\u043D \u043D\u0435 \u0432\u043A\u0430\u0437\u0430\u043D\u043E'}</div>
                <button
                  type="button"
                  onClick={() => setIsEditOpen(true)}
                  style={{
                    marginTop: 12,
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    color: '#111827',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  {'\u0420\u0435\u0434\u0430\u0433\u0443\u0432\u0430\u0442\u0438 \u043F\u0440\u043E\u0444\u0456\u043B\u044C'}
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card style={{ marginTop: 16, border: 'none', boxShadow: 'none', background: 'transparent' }}>
          <div style={{ padding: 20 }}>
            <div
              style={{
                marginBottom: 16,
                padding: '8px 0 12px',
                display: 'grid',
                gridTemplateColumns: isNarrow ? '1fr' : 'repeat(2, minmax(0,1fr))',
                gap: isNarrow ? 10 : 0,
                borderTop: '1px solid #e5e7eb',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <StatInline label={'\u0414\u043E\u0434\u0430\u043D\u043E \u0434\u043E \u043A\u043D\u0438\u0433\u043E\u043E\u0431\u0456\u0433\u0443'} value={uniqueAdded} />
              <StatInline label={'\u0412\u0437\u044F\u0442\u043E \u0434\u043B\u044F \u043F\u0440\u043E\u0447\u0438\u0442\u0430\u043D\u043D\u044F'} value={booksTaken} bordered={!isNarrow} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginTop: 22, marginBottom: 10 }}>
              <h4 style={{ margin: 0 }}>{'\u0412\u0438\u0431\u0440\u0430\u043D\u0456 \u043A\u0430\u0432\u2019\u044F\u0440\u043D\u0456'}</h4>
              <div style={{ color: '#6b7280', fontSize: 13 }}>{`${favoriteCafes.length} ${favoriteCafes.length === 1 ? '\u043A\u0430\u0432\u2019\u044F\u0440\u043D\u044F' : '\u043A\u0430\u0432\u2019\u044F\u0440\u043D\u0456'}`}</div>
            </div>
            {loadingFavoriteCafes ? (
              <div style={{ color: '#6b7280' }}>{'\u0417\u0430\u0432\u0430\u043D\u0442\u0430\u0436\u0435\u043D\u043D\u044F\u2026'}</div>
            ) : favoriteCafes.length === 0 ? (
              <div style={{ color: '#6b7280' }}>{'\u0412\u0438 \u0449\u0435 \u043D\u0435 \u0434\u043E\u0434\u0430\u043B\u0438 \u0436\u043E\u0434\u043D\u043E\u0457 \u043A\u0430\u0432\u2019\u044F\u0440\u043D\u0456 \u0443 \u0432\u0438\u0431\u0440\u0430\u043D\u0456.'}</div>
            ) : (
              <div style={{ display: 'grid', gap: 0 }}>
                {favoriteCafes.map((c, idx) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: idx < favoriteCafes.length - 1 ? '1px solid #e5e7eb' : 'none',
                    }}
                  >
                    <div>
                      <Link to={`/cafe/${encodeURIComponent(c.name)}`} style={{ textDecoration: 'none', fontWeight: 700, color: '#111827' }}>
                        {c.name}
                      </Link>
                      <div style={{ color: '#6b7280', fontSize: 14 }}>{c.address}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => openCafeMap(c)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: '1px solid #cbd5e1',
                          background: '#fff',
                          color: '#334155',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {'\u041D\u0430 \u043C\u0430\u043F\u0456'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmRemoveCafe(c)}
                        disabled={favoriteBusyId === c.id}
                        title={'\u0412\u0438\u0434\u0430\u043B\u0438\u0442\u0438 \u0437 \u0432\u0438\u0431\u0440\u0430\u043D\u0438\u0445'}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 999,
                          border: '1px solid #e5e7eb',
                          background: '#fff',
                          color: '#6b7280',
                          cursor: favoriteBusyId === c.id ? 'default' : 'pointer',
                          lineHeight: '18px',
                          fontWeight: 700,
                          padding: 0,
                        }}
                      >
                        {'\u00D7'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>


        <Card style={{ marginTop: 16, border: 'none', boxShadow: 'none', background: 'transparent' }}>
          <div style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
              <h3 style={{ margin: 0 }}>{'\u041E\u0441\u0442\u0430\u043D\u043D\u0456 \u043F\u0440\u043E\u0447\u0438\u0442\u0430\u043D\u0456 \u043A\u043D\u0438\u0433\u0438'}</h3>
              <Link to="/profile/read" style={{ textDecoration: 'none', color: '#14532d', fontWeight: 600 }}>
                {'\u041F\u043E\u0431\u0430\u0447\u0438\u0442\u0438 \u0431\u0456\u043B\u044C\u0448\u0435 \u2192'}
              </Link>
            </div>
            <div style={{ marginTop: 8, borderTop: '1px solid #e5e7eb' }} />

            {loadingBooks ? (
              <div style={{ color: '#6b7280', marginTop: 8 }}>{'\u0417\u0430\u0432\u0430\u043D\u0442\u0430\u0436\u0435\u043D\u043D\u044F\u2026'}</div>
            ) : readBooks.length === 0 ? (
              <div style={{ color: '#6b7280', marginTop: 8 }}>{'\u0429\u0435 \u043D\u0435\u043C\u0430\u0454 \u043F\u0440\u043E\u0447\u0438\u0442\u0430\u043D\u0438\u0445 \u043A\u043D\u0438\u0433.'}</div>
            ) : (
              <div
                style={{
                  marginTop: 12,
                  display: 'grid',
                  gridTemplateColumns: isNarrow ? 'repeat(2, minmax(0,1fr))' : 'repeat(4, minmax(0,1fr))',
                  gap: 16,
                }}
              >
                {readBooks.map((b) => (
                  <BookCard
                    key={b.id}
                    title={b.title}
                    author={b.author}
                    coverUrl={b.coverUrl}
                    onClick={() => b.isbn && navigate(`/book/${encodeURIComponent(b.isbn)}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>

        {isEditOpen && (
          <div
            onClick={() => setIsEditOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.45)',
              zIndex: 70,
              display: 'grid',
              placeItems: 'center',
              padding: 12,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(520px, 100%)',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                padding: 16,
              }}
            >
              <h3 style={{ margin: '0 0 10px 0' }}>{'\u0420\u0435\u0434\u0430\u0433\u0443\u0432\u0430\u0442\u0438 \u043F\u0440\u043E\u0444\u0456\u043B\u044C'}</h3>
              <Label>{'\u0406\u043C\u2019\u044F'}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={'\u0412\u0430\u0448\u0435 \u0456\u043C\u2019\u044F'} />
              <Label>Email</Label>
              <Input value={user.email || ''} disabled />
              <Label>{'\u0422\u0435\u043B\u0435\u0444\u043E\u043D'}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={'+380\u2026'} />
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    setName(initial.name);
                    setPhone(initial.phone);
                    setIsEditOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    color: '#111827',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {'\u0421\u043A\u0430\u0441\u0443\u0432\u0430\u0442\u0438'}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await handleSave();
                    setIsEditOpen(false);
                  }}
                  disabled={!hasChanges}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid',
                    borderColor: hasChanges ? '#14532d' : '#d1d5db',
                    background: hasChanges ? '#16a34a' : '#f3f4f6',
                    color: hasChanges ? '#fff' : '#6b7280',
                    cursor: hasChanges ? 'pointer' : 'not-allowed',
                    fontWeight: 700,
                  }}
                >
                  {'\u0417\u0431\u0435\u0440\u0435\u0433\u0442\u0438'}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmRemoveCafe && (
          <div
            onClick={() => setConfirmRemoveCafe(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.45)',
              zIndex: 75,
              display: 'grid',
              placeItems: 'center',
              padding: 12,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(460px, 100%)',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                boxShadow: '0 20px 45px rgba(2,6,23,0.18)',
                padding: 16,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
                {'\u0412\u0438\u0434\u0430\u043B\u0438\u0442\u0438 \u043A\u0430\u0432\u2019\u044F\u0440\u043D\u044E \u0437 \u0432\u0438\u0431\u0440\u0430\u043D\u0438\u0445?'}
              </div>
              <div style={{ color: '#4b5563', marginBottom: 16, lineHeight: 1.4 }}>
                {confirmRemoveCafe.name}
                {confirmRemoveCafe.address ? `, ${confirmRemoveCafe.address}` : ''}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setConfirmRemoveCafe(null)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    color: '#111827',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  {'\u0421\u043A\u0430\u0441\u0443\u0432\u0430\u0442\u0438'}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const cafeId = confirmRemoveCafe.id;
                    setConfirmRemoveCafe(null);
                    await removeFavoriteCafe(cafeId);
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid #b91c1c',
                    background: '#b91c1c',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  {'\u0412\u0438\u0434\u0430\u043B\u0438\u0442\u0438'}
                </button>
              </div>
            </div>
          </div>
        )}

        {isQrPreviewOpen && (
          <div
            onClick={() => setIsQrPreviewOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(2, 6, 23, 0.85)',
              zIndex: 90,
              display: 'grid',
              placeItems: 'center',
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(92vw, 620px)',
                aspectRatio: '1 / 1',
                background: '#fff',
                borderRadius: 14,
                border: '1px solid #e5e7eb',
                display: 'grid',
                placeItems: 'center',
                position: 'relative',
              }}
            >
              <button
                type="button"
                onClick={() => setIsQrPreviewOpen(false)}
                aria-label={'\u0417\u0430\u043A\u0440\u0438\u0442\u0438'}
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#111827',
                  cursor: 'pointer',
                  fontWeight: 700,
                  lineHeight: '30px',
                  padding: 0,
                }}
              >
                {'\u00D7'}
              </button>
              <QRCode value={user.uid} size={Math.min(520, Math.max(240, window.innerWidth - 56))} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{ fontSize: 14, color: '#111827', marginTop: 8, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function StatInline({ label, value, bordered = false }) {
  return (
    <div style={{ padding: '4px 10px', borderLeft: bordered ? '1px solid #e5e7eb' : 'none' }}>
      <div style={{ color: '#6b7280', marginBottom: 4, fontSize: 13 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 900, color: '#111827', lineHeight: 1 }}>{value ?? 0}</div>
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      style={{
        height: 36,
        padding: '8px 10px',
        borderRadius: 10,
        border: '1px solid #e5e7eb',
        outline: 'none',
        fontSize: 14,
        width: '100%',
        background: props.disabled ? '#f9fafb' : '#fff',
        color: props.disabled ? '#6b7280' : '#111827',
      }}
    />
  );
}

function BookCard({ title, author, coverUrl, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: 10,
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: '100%',
          aspectRatio: '3 / 4.2',
          background: '#f3f4f6',
          borderRadius: 8,
          backgroundImage: coverUrl ? `url(${coverUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          marginBottom: 10,
        }}
      />
      <div style={{ fontWeight: 700, lineHeight: 1.25 }}>{title || '\u0411\u0435\u0437 \u043D\u0430\u0437\u0432\u0438'}</div>
      <div style={{ color: '#6b7280', fontSize: 14 }}>{author || '\u0410\u0432\u0442\u043E\u0440 \u043D\u0435\u0432\u0456\u0434\u043E\u043C\u0438\u0439'}</div>
    </button>
  );
}

